<template>
  <v-container v-if="currentClient && teams[$route.params.team].account">
    <v-delete-modal
      name="delete-client-modal"
      :title="$t('modals.deleteClient.title', { name: $h.formatName(currentClient) })"
      :text="$t('modals.deleteClient.text')"
      :deleteHandler="
        () => {
          deleteClientModalHandler(this.currentClient.uid);
        }
      "
    >
    </v-delete-modal>
    <v-delete-modal
      name="delete-project-modal"
      :title="
        $t('modals.deleteProject.title', {
          name: $h.getNameFromProjectsByUid(currentDeleteProjectUid, currentClientProjects),
        })
      "
      :text="$t('modals.deleteProject.text')"
      :deleteHandler="
        () => {
          deleteProjectModalHandler(currentDeleteProjectUid);
        }
      "
    >
    </v-delete-modal>
    <v-row>
      <v-col stretch>
        <v-breadcrumb :items="breadcrumbClientItems" :mobile="breadcrumbMobileItem"></v-breadcrumb>
      </v-col>
    </v-row>
    <v-row>
      <v-col stretch>
        <v-title-bar :title="$h.formatName(currentClient)" :label="$t('labels.newClientProject')">
          <v-button round secondary icon @click.native="$goTo('createProject')"
            ><v2-icon :data="require('@icon/Icon=folder.svg')" color="#fff"></v2-icon
          ></v-button>
          <v-button
            round
            primary
            icon
            @click.native="
              $goTo('QuickLaunchWithClient', {
                client: currentClient.uid,
              })
            "
            class="ml-2"
            ><v2-icon
              :data="require('@icon/Icon=quick-launch.svg')"
              :original="true"
              fill
              height="25"
              width="auto"
            ></v2-icon
          ></v-button>
        </v-title-bar>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="col-12">
        <v-list-header simple>
          <v-list-header-cell :title="$t('titles.clientInfo')" />
          <v-list-header-cell
            :link="$t('actions.edit')"
            :route="{ name: 'editClient', params: { uid: currentClient.uid } }"
          />
        </v-list-header>
        <ul class="list">
          <v-list-item simple>
            <v-list-cell :title="$t('client.name')" size="2" />
            <v-list-cell :value="$h.formatName(currentClient)" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.email')" size="2" />
            <v-list-cell :value="currentClient.email" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.locale')" size="2" />
            <v-list-cell :value="$h.formatLocale(currentClient)" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.gender')" size="2" />
            <v-list-cell :value="$h.formatGender(currentClient.gender)" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.company')" size="2" />
            <v-list-cell :value="currentClient.company" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.address')" size="2" />
            <v-list-cell :value="$h.formatAddress(currentClient)" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.tel')" size="2" />
            <v-list-cell :value="currentClient.tel" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.mobile')" size="2" />
            <v-list-cell :value="currentClient.mobile" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.fax')" size="2" />
            <v-list-cell :value="currentClient.fax" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.website')" size="2" />
            <v-list-cell :value="currentClient.website" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.reference')" size="2" />
            <v-list-cell :value="currentClient.reference" size="2" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple flexibleHeight>
            <v-list-cell :title="$t('client.notes')" size="2" />
            <v-list-cell :value="currentClient.notes" size="2" :truncate="false" />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item simple>
            <v-list-cell :title="$t('client.created')" size="2" />
            <v-list-cell
              :value="$h.formatDate(currentClient.created_at, teams[$route.params.team].account.timezone)"
              size="2"
            />
            <v-list-cell emptyValue />
          </v-list-item>
          <v-list-item
            flexibleHeight
            simple
            v-for="(customField, index) in filteredCurrentClientCustomFields"
            :key="index"
          >
            <v-list-cell :title="getCustomFieldTitle(customField, index)" size="2" />
            <v-list-cell
              v-if="!editCustomField[index] && !customFieldIsLoading[index]"
              :has_html="true"
              :value="getCustomFieldValue(customField, index, currentClient)"
              size="2"
              :placeholder="getCustomFieldPlaceholder(customField, index)"
              :truncate="false"
            />
            <v-list-cell size="2" v-if="editCustomField[index] && !customFieldIsLoading[index]">
              <div slot="value" class="w-full">
                <v2-select
                  withoutFormError
                  :placeholder="$t('placeholders.selectCustom')"
                  v-if="editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'select'"
                  v-model="customFieldValue[index]"
                  :options="customFieldSelectOptions[index]"
                  optionLabel="label"
                  :searchable="true"
                  openDir="bottom"
                ></v2-select>
                <v2-select
                  withoutFormError
                  :placeholder="$t('placeholders.selectCustom')"
                  v-if="editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'multiselect'"
                  :closeOnSelect="false"
                  :clearOnSelect="false"
                  v-model="customFieldValue[index]"
                  :options="customFieldSelectOptions[index]"
                  optionLabel="label"
                  trackBy="label"
                  :multiple="true"
                  :searchable="true"
                  openDir="bottom"
                ></v2-select>
                <date-picker
                  class="envivo-datetimepicker"
                  v-if="editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'datetime'"
                  v-model="customFieldValue[index]"
                  :lang="teams[$route.params.team].account.locale"
                  type="datetime"
                  :formatter="datetimeFormat"
                  :minute-step="1"
                  :first-day-of-week="1"
                >
                </date-picker>
                <v-textfield
                  simple
                  v-if="editCustomField[index] && !customFieldIsLoading[index] && customField.type === 'input'"
                  v-model="customFieldValue[index]"
                ></v-textfield>
                <v-pulse-loader
                  v-if="customFieldIsLoading[index]"
                  :loading="customFieldIsLoading[index]"
                  :color="'#343e93'"
                  :size="'10px'"
                ></v-pulse-loader>
              </div>
            </v-list-cell>
            <v-list-cell
              v-if="!customField.readonly"
              :button="editCustomField[index] ? $t('actions.save') : $t('actions.edit')"
              :onClick="
                () => {
                  toggleCustomField(customField, index);
                }
              "
            />
            <v-list-cell v-if="customField.readonly" emptyValue />
          </v-list-item>
        </ul>
      </v-col>
    </v-row>
    <v2-row v-if="currentClient">
      <v2-col col12 noSpace>
        <v-list-header simple>
          <v-list-header-cell :title="$t('labels.portalLink')" />
        </v-list-header>
      </v2-col>
      <v2-col col12 v-if="teams[$route.params.team].account.team.attributes.portal_enabled">
        <v2-project-link
          :link="currentClient.links.portal"
          :label="$t('quickLaunch.labels.portalLink')"
          :clickCallback="
            () => {
              copyLink(currentClient.links.portal);
            }
          "
        ></v2-project-link>
      </v2-col>
      <v2-col col12 v-if="teams[$route.params.team].account.team.attributes.portal_enabled">
        <label
          style="
            margin-bottom: 0.25rem;
            font-size: 0.75rem;
            line-height: 1rem;
            text-transform: uppercase;
            color: rgba(174, 174, 174, 1);
          "
          >{{ $t("projectShare.labels.setDefaultProject") }}</label
        >
        <div
          class="flex justify-between items-center p-2 flex-wrap"
          style="background-color: rgba(241, 242, 246, 1); border-radius: 0.5rem"
        >
          <div style="color: rgba(174, 174, 174, 1)">{{ $t("projectShare.texts.setDefaultProject") }}</div>
          <div>
            <v2-select
              v-if="currentClientProjects"
              withoutFormError
              :placeholder="$t('placeholders.selectProject')"
              v-model="defaultProject"
              :options="currentClientProjects"
              :allowEmpty="true"
              optionLabel="title"
              id="defaultProject"
              name="defaultProject"
              @select="setDefaultProject"
              @remove="setDefaultProject(null)"
              style="width: 300px"
            ></v2-select>
          </div>
        </div>
      </v2-col>
    </v2-row>
    <v-row>
      <v-col class="col-12">
        <v-list-header simple>
          <v-list-header-cell :title="$t('titles.activityFeeds')" />
        </v-list-header>
        <p>{{ $t("texts.activityFeedsClient") }}</p>
        <ul>
          <v-list-item>
            <v-list-cell :title="$t('titles.activityFeeds')" />
            <v-list-cell class="justify-end" v-if="currentClient">
              <v-switch slot="value" v-model="isSubscribed" name="isSubscribed" @input="toggleSubscription"></v-switch>
            </v-list-cell>
          </v-list-item>
        </ul>
      </v-col>
    </v-row>
    <v-row v-if="currentClientTags">
      <v-col stretch>
        <v-list-header simple>
          <v-list-header-cell :title="$t('titles.tags')" />
          <v-list-header-cell
            :link="$t('actions.edit')"
            :route="{ name: 'editClient', params: { uid: currentClient.uid } }"
          />
        </v-list-header>
        <v-tags class="mb-12" :tags="currentClientTags"></v-tags>
      </v-col>
    </v-row>
    <v-row
      v-if="
        teamsSettings[$route.params.team] && teamsSettings[$route.params.team].listsSettings.clientProjects.filterActive
      "
    >
      <v-col stretch>
        <v-card custom class="p-6">
          <div slot="content" class="flex flex-col sm:flex-row w-full justify-between">
            <div>
              <div class="text-grey-lavender mb-1">
                {{ $t("filters.results") }}
              </div>
              <h6>
                {{
                  $tc(
                    "filters.projectsResult",
                    teamsSettings[$route.params.team].listsSettings.clientProjects.pagination.total,
                    {
                      result: teamsSettings[$route.params.team].listsSettings.clientProjects.pagination.total,
                    }
                  )
                }}
              </h6>
            </div>
            <v-button class="mt-4 sm:mt-0 mr-2" attention @click.native="resetFilters(setClientProjectsFilters)">{{
              $t("filters.resetFilter")
            }}</v-button>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="currentClientProjects && teamsSettings[$route.params.team]">
      <v-col class="col-12">
        <v-list-header simple>
          <v-list-header-cell
            :title="
              $tc(
                'titles.projectsFor',
                currentClient.projects_count,
                { count: currentClient.projects_count },
                { name: $h.formatName(currentClient, true) }
              )
            "
          />
          <v-list-header-cell
            :link="$t('labels.newClientProject')"
            :route="{
              name: 'createProject',
              params: { uid: currentClient.uid },
            }"
          />
        </v-list-header>
        <v-list-header complex>
          <v-list-header-cell emptyIcon />
          <v-list-header-cell
            size="3"
            :showAs="$t('project.title')"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="sortMainColDropdown"
          />
          <v-list-header-cell
            :showAs="$t(teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.firstCol.label)"
            :size="teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.firstCol.size"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="showAsFirstColDropdown"
          />
          <v-list-header-cell
            :showAs="$t(teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.secondCol.label)"
            tabletHidden
            :size="teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.secondCol.size"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="showAsSecondColDropdown"
          />
          <v-list-header-cell
            :showAs="$t(teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.thirdCol.label)"
            dropdownRight
            :size="teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.thirdCol.size"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="showAsThirdColDropdown"
          />
          <v-list-header-cell
            :mobile="$t('actions.sort')"
            :mobileDropdownUid="$h.createUniqueUid()"
            :mobileDropdown="mobileDropdown"
          />
          <v-list-header-cell emptyIcon />
          <v-list-header-cell
            filter
            dropdownRight
            multiselect
            :columns="3"
            :filterActive="teamsSettings[$route.params.team].listsSettings.clientProjects.filterActive"
            :filterDropdownUid="$h.createUniqueUid()"
            :filterDropdown="filterDropdown"
          />
        </v-list-header>
        <ul class="list">
          <router-link
            :to="{
              name: 'Project',
              params: { uid: project.uid, tab: 'overview' },
            }"
            v-for="project in currentClientProjects"
            :key="project.uid"
          >
            <v-list-item complex hover>
              <v-list-cell
                icon="Icon=folder-project.svg"
                :iconFill="true"
                :iconColor="$h.formatStatusColor(project.status)"
              />
              <v-list-cell
                :title="project.title"
                size="3"
                :mobileSubtitle="
                  setStatus(project.status) +
                  ' • ' +
                  $h.formatActivity(project, teams[$route.params.team].account.timezone)
                "
              />
              <v-list-cell
                :size="teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.firstCol.size"
                :value="
                  $h.formatValue(
                    teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.firstCol.format,
                    project[teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.firstCol.key],
                    teams[$route.params.team].account.timezone
                  )
                "
              />
              <v-list-cell
                :size="teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.secondCol.size"
                tabletHidden
                :value="
                  $h.formatValue(
                    teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.secondCol.format,
                    project[teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.secondCol.key],
                    teams[$route.params.team].account.timezone
                  )
                "
              />
              <v-list-cell
                :size="teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.thirdCol.size"
                :value="
                  $h.formatValue(
                    teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.thirdCol.format,
                    project[teamsSettings[$route.params.team].listsSettings.clientProjects.showAs.thirdCol.key],
                    teams[$route.params.team].account.timezone
                  )
                "
              />
              <v-list-cell size="0" showAlways>
                <v2-button
                  secondary
                  round
                  slot="value"
                  @click.native.prevent="
                    $goTo('QuickLaunchWithClientAndProject', {
                      client: project.owner.uid,
                      project: project.uid,
                    })
                  "
                >
                  <v2-icon :data="require('@icon/Icon=quick-launch.svg')" :original="true" fill></v2-icon>
                </v2-button>
              </v-list-cell>
              <v-list-cell
                iconDropdown
                grey
                icon="Icon=more-horizontal.svg"
                iconColor="#5057af"
                :dropdownData="moreDropdown"
                :dropdownUid="$h.createUniqueUid()"
                :dropdownValue="project"
              />
            </v-list-item>
          </router-link>
          <v-list-item v-if="!currentClientProjects.length">
            <v-list-cell :value="$t('placeholders.noProjects')"></v-list-cell>
          </v-list-item>
        </ul>
        <v-list-pagination
          :currentPage="teamsSettings[$route.params.team].listsSettings.clientProjects.pagination.page"
          :showPerPage="teamsSettings[$route.params.team].listsSettings.clientProjects.pagination.perPage"
          :totalPages="teamsSettings[$route.params.team].listsSettings.clientProjects.pagination.lastPage"
          :apiCall="
            (page, perPage) => {
              changeClientProjectsPage(page, perPage);
            }
          "
        >
        </v-list-pagination>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <h5>{{ $t("labels.clientOptions") }}</h5>
        <v-button danger @click.native="showDeleteClientModal()">{{ $t("actions.deleteClient") }}</v-button>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import listSettings from "@/mixins/listSettings.js";
import clientProjects from "@/mixins/dropdowns/clientProjects.js";
import customFields from "@/mixins/customFields.js";
import * as clipboard from "clipboard-polyfill";

export default {
  name: "Client",
  mixins: [listSettings, clientProjects, customFields],
  data() {
    return {
      isLoading: false,
      tags: [],
      tagsOptions: [],
      defaultProject: {},
      defaultProjectOptions: [],
      currentDeleteProjectUid: null,
      isSubscribed: false,
      moreDropdown: [
        {
          options: [
            {
              label: this.$t("actions.delete"),
              method: (project) => {
                this.showDeleteProjectModal(project.uid);
              },
              color: "text-danger",
              doNotShowActiveState: true,
            },
          ],
        },
      ],
      breadcrumbClientItems: [
        {
          label: this.$t("menus.clients"),
          route: { name: "Clients" },
        },
      ],
      breadcrumbMobileItem: {
        label: this.$t("menus.clients"),
        route: { name: "Clients" },
      },
    };
  },
  created() {
    this.loadPageData(this.$route.params.uid);
  },
  methods: {
    ...mapActions([
      "setListsSettings",
      "getClient",
      "getClientTags",
      "getClientProjects",
      "getClientSubscription",
      "deleteClient",
      "deleteProject",
      "postClientSubscription",
      "deleteClientSubscription",
      "getTeamTemplates",
      "getTeamContacts",
      "setProjectsCustomFields",
      "addCustomFilterToListsettings",
      "editClient",
      "getUsers",
      "getTeamTags",
    ]),
    setCustomFields(customFields) {
      var init = false;
      if (!this.projectsCustomFields) init = true;
      this.setProjectsCustomFields(customFields).then(() => {
        if (init) this.setCustomFilters();
      });
    },
    setCustomFilters() {
      if (this.projectsCustomFields) {
        Object.keys(this.projectsCustomFields).forEach((key) => {
          if (this.projectsCustomFields[key].filterable) {
            let newWithOption = {
              label: this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                ? this.$t("filters.with") +
                  " " +
                  this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                : this.$t("filters.with") + " " + this.projectsCustomFields[key].title.default,
              column: 2,
              select: {
                custom: true,
                name: "with_custom_" + key,
                multiple: true,
                model: [],
                options: [],
                label: "label",
                placeholder: this.$t("placeholders.selectCustom"),
                wrap: true,
                searchable: true,
                searchChange: () => {},
              },
              method: () => {},
              checkbox: true,
              hoverBg: true,
              hideIfNoOptions: true,
            };
            this.$set(newWithOption, "active", false);
            let newWithoutOption = {
              label: this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                ? this.$t("filters.without") +
                  " " +
                  this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                : this.$t("filters.without") + " " + this.projectsCustomFields[key].title.default,
              column: 3,
              select: {
                custom: true,
                name: "without_custom_" + key,
                multiple: true,
                model: [],
                options: [],
                label: "label",
                placeholder: this.$t("placeholders.selectCustom"),
                wrap: true,
                searchable: true,
                searchChange: () => {},
              },
              method: () => {},
              checkbox: true,
              hoverBg: true,
              hideIfNoOptions: true,
            };
            this.$set(newWithoutOption, "active", false);
            this.addCustomFilterToListsettings({
              list: "clientProjects",
              customFilter: "with_custom_" + key,
            });
            this.addCustomFilterToListsettings({
              list: "clientProjects",
              customFilter: "without_custom_" + key,
            });
            const index = this.filterDropdown[0].options.length - 3;
            this.filterDropdown[0].options.splice(index, 0, newWithoutOption);
            this.filterDropdown[0].options.splice(index, 0, newWithOption);
          }
        });
      }
    },
    loadPageData(uid) {
      const vm = this;
      vm.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      this.setCustomFilters();
      vm.getClient({ client: uid })
        .then(() => {
          vm.defaultProject = vm.currentClient.default_project;
          vm.breadcrumbClientItems.push({
            label: vm.$h.formatName(vm.currentClient),
            active: true,
          });
          this.setEditableCustomFields(this.filteredCurrentClientCustomFields, this.currentClient);
          vm.$store.commit("PROJECTS_SET_NEW_PROJECT_CLIENT", vm.currentClient);
          let p = {
            search: vm.teamsSettings[vm.$route.params.team].listsSettings.clientProjects.search,
            page: vm.teamsSettings[vm.$route.params.team].listsSettings.clientProjects.pagination.page,
            per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.clientProjects.pagination.perPage,
            order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.clientProjects.order.col,
            order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.clientProjects.order.dir,
          };
          let params = vm.setFilterRequestObjectFromStore("clientProjects", p);
          const uid = vm.currentClient.uid;
          const p1 = vm
            .getClientProjects({
              client: uid,
              setListsSettings: true,
              params,
            })
            .then((res) => {
              vm.setCustomFields(res.data.meta.custom);
            })
            .catch((err) => {
              console.log(">>>>>>>>>>>>>>> err", err);
            });
          const p2 = vm.getTeamTemplates({ params: { per_page: "1000" } });
          params = {
            per_page: "1000",
            order_col: "last_name",
            order_dir: "ASC",
            search: "",
          };
          const p3 = vm.getTeamContacts({ params, customLoading: true });
          const p4 = vm.getClientTags({ params, client: uid }).then(() => {
            this.formatTagsOptions(JSON.parse(JSON.stringify(this.currentClientTags)));
            this.tags = JSON.parse(JSON.stringify(this.tagsOptions));
            this.initTagOptions();
          });
          const p5 = vm.getClientSubscription({ client: uid }).then(() => {
            vm.isSubscribed = vm.currentClientSubscription;
          });
          const p6 = this.getUsers({ params, customLoading: true });
          const p7 = this.getTeamTags({ params, customLoading: true });
          Promise.all([p1, p2, p3, p4, p5, p6, p7])
            .then(() => {
              vm.initSort("clientProjects");
              vm.initShowAs("clientProjects");
              vm.initFilters("clientProjects");
              vm.setDropdownOptions();
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch((err) => {
              console.log(">>>>>>>>>>>>>>> ERROR", err);
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        })
        .catch((err) => {
          console.log(">>>>>>>>>>>>>>> ERROR", err);
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("toasts.resourceNotFound.client"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    sortClientProjects(col, dir) {
      let params = {
        search: this.searchQuery,
        page: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.pagination.page,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.pagination.perPage,
        order_col: col,
        order_dir: dir,
      };
      this.getThisClientProjects(params);
    },
    changeClientProjectsPage(page, perPage) {
      let params = {
        search: this.searchQuery,
        page,
        per_page: perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.order.dir,
      };
      this.getThisClientProjects(params);
    },
    setClientProjectsFilters() {
      let params = {
        search: this.searchQuery,
        page: 1,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.pagination.perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.order.dir,
      };
      this.getThisClientProjects(params);
      this.$eventbus.$emit("closeAllDropdowns");
    },
    getThisClientProjects(params) {
      this.filterDropdown[0].options.forEach((option) => {
        if (option.active && option.switch && option.switch.name && !option.select) {
          params[option.switch.name] = option.switch.model;
        }
        if (option.active && option.select && !option.select.custom) {
          params[option.select.name] = this.transformModelToRequestArray(option.select.model);
        }
        if (option.active && option.select && option.select.custom) {
          const filterName = option.select.name.split("_").slice(0, 2).join("_");
          const customFieldName = option.select.name.split("_").slice(2).join("_");
          if (!params[filterName]) params[filterName] = [];
          this.transformModelToRequestArray(option.select.model, customFieldName).forEach((model) => {
            params[filterName].push(model);
          });
        }
        if (option.dateTimeRange) {
          if (
            option.dateTimeRange.model.length === 2 &&
            option.dateTimeRange.model[0] !== "" &&
            option.dateTimeRange.model[1] !== ""
          ) {
            params["time_from"] = this.$moment(option.dateTimeRange.model[0])
              .tz(this.teams[this.$route.params.team].account.timezone)
              .format();
            params["time_to"] = this.$moment(option.dateTimeRange.model[1])
              .tz(this.teams[this.$route.params.team].account.timezone)
              .format();
          } else {
            params["time_from"] = "";
            params["time_to"] = "";
          }
        }
      });
      if (params.time_relative && params.time_relative !== "custom" && params.time_from && params.time_to) {
        params.time_from = "";
        params.time_to = "";
      }
      this.getClientProjects({
        client: this.$route.params.uid,
        setListsSettings: true,
        params,
      }).then(() => {
        this.initSort("clientProjects");
      });
    },
    setStatus(status) {
      let active = this.$t("stati.listValues.new");
      if ((status & this.projectsStati["POSTED"].bit) === this.projectsStati["POSTED"].bit) {
        active = this.$t("stati.listValues.sent");
      }
      if ((status & this.projectsStati["VISITED"].bit) === this.projectsStati["VISITED"].bit) {
        active = this.$t("stati.listValues.visited");
      }
      return active;
    },
    showDeleteClientModal() {
      this.$modal.show("delete-client-modal");
    },
    deleteClientModalHandler() {
      this.$modal.hide("delete-client-modal");
      this.deleteClient({ client: this.currentClient.uid })
        .then(() => {
          this.$goTo("Clients");
          this.$snotify.success(this.$t("toasts.deleteClient.success"), this.$t("toasts.success"), {
            icon: this.$successIcon,
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            this.$snotify.error(this.$t("toasts.deleteClient.error"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
          }
        });
    },
    copyLink(link) {
      clipboard.writeText(link);
      this.$snotify.success(this.$t("toasts.copiedLink.success"), this.$t("toasts.success"), {
        icon: this.$successIcon,
      });
    },
    showDeleteProjectModal(uid) {
      this.currentDeleteProjectUid = uid;
      this.$modal.show("delete-project-modal");
    },
    deleteProjectModalHandler(uid) {
      this.currentDeleteProjectUid = null;
      this.$modal.hide("delete-project-modal");
      this.deleteProject({ project: uid })
        .then(() => {
          this.getClient({ client: this.currentClient.uid }).then(() => {
            let params = {
              search: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.search,
              page: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.pagination.page,
              per_page: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.pagination.perPage,
              order_col: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.order.col,
              order_dir: this.teamsSettings[this.$route.params.team].listsSettings.clientProjects.order.dir,
            };
            this.getThisClientProjects(params);
            this.$snotify.success(this.$t("toasts.deleteProject.success"), this.$t("toasts.success"), {
              icon: this.$successIcon,
            });
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            this.$snotify.error(this.$t("toasts.deleteProject.error"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
          }
        });
    },
    toggleSubscription() {
      this.isSubscribed
        ? this.postClientSubscription({
            customLoading: true,
            client: this.currentClient.uid,
          })
            .then(() => {
              this.$snotify.success(this.$t("toasts.setSubscription.success"), this.$t("toasts.success"), {
                icon: this.$successIcon,
              });
            })
            .catch((err) => {
              if (err.response && !err.response.data.error.custom) {
                this.$snotify.error(this.$t("toasts.setSubscription.error"), this.$t("toasts.error"), {
                  timeout: 0,
                  icon: this.$errorIcon,
                });
              }
            })
        : this.deleteClientSubscription({
            customLoading: true,
            client: this.currentClient.uid,
          })
            .then(() => {
              this.$snotify.success(this.$t("toasts.deleteSubscription.success"), this.$t("toasts.success"), {
                icon: this.$successIcon,
              });
            })
            .catch((err) => {
              if (err.response && !err.response.data.error.custom) {
                this.$snotify.error(this.$t("toasts.deleteSubscription.error"), this.$t("toasts.error"), {
                  timeout: 0,
                  icon: this.$errorIcon,
                });
              }
            });
    },
    setContactsDropdownOptions() {
      let contactOptions = [];
      if (this.contacts) {
        this.contacts.forEach((contact) => {
          const name = this.$h.formatName(contact);
          contactOptions.push({ label: name, value: contact.uid });
        });
      }
      this.filterDropdown[0].options.forEach((option) => {
        if (option.select) {
          if (option.select.name === "with_contact" || option.select.name === "without_contact") {
            option.select.options = contactOptions;
          }
        }
      });
    },
    toggleCustomField(customField, index) {
      if (this.editCustomField[index]) {
        this.$set(this.customFieldIsLoading, index, true);
        let custom = {};
        if (this.filteredCurrentClientCustomFields[index].type === "select") {
          custom[index] = [this.customFieldValue[index].value];
        } else if (this.filteredCurrentClientCustomFields[index].type === "multiselect") {
          custom[index] = [];
          this.customFieldValue[index].forEach((val) => {
            custom[index].push(val.value);
          });
        } else {
          custom[index] = this.customFieldValue[index];
        }
        let data = {
          custom,
        };
        this.editClient({
          customLoading: true,
          params: { client: this.currentClient.uid },
          data,
        })
          .then(() => {
            this.$snotify.success(this.$t("toasts.editedProject.success"), this.$t("toasts.success"), {
              icon: this.$successIcon,
            });
            this.$set(this.editCustomField, index, false);
            this.$set(this.customFieldIsLoading, index, false);
          })
          .catch((err) => {
            if (err.response && !err.response.data.error.custom) {
              this.$snotify.error(this.$t("toasts.editedProject.error"), this.$t("toasts.error"), {
                timeout: 0,
                icon: this.$errorIcon,
              });
            }
            this.$set(this.customFieldIsLoading, index, false);
          });
      } else {
        this.$set(this.editCustomField, index, true);
      }
    },
    setUsersDropdownOptions() {
      let userOptions = [];
      if (this.users) {
        this.users.forEach((user) => {
          const name = this.$h.formatName(user);
          userOptions.push({ label: name, value: user.uid });
        });
      }
      this.filterDropdown[0].options.forEach((option) => {
        if (option.select) {
          if (option.select.name === "with_user" || option.select.name === "without_user") {
            option.select.options = userOptions;
          }
        }
      });
    },
    formatTagsOptions(tagsObjects) {
      this.tagsOptions = [];
      Object.keys(tagsObjects).forEach((key) => {
        this.tagsOptions.push(tagsObjects[key].name);
      });
    },
    addTag(newTag) {
      this.tagsOptions.push(newTag);
      this.tags.push(newTag);
    },
    initTagOptions() {
      this.isLoading = true;
      const params = {
        per_page: "1000",
      };
      this.getTeamTags({ params, customLoading: true }).then((res) => {
        this.formatTagsOptions(JSON.parse(JSON.stringify(res)));
        this.isLoading = false;
      });
    },
    setDefaultProject(defaultProject) {
      const vm = this;
      const data = {
        default_project_uid: defaultProject ? defaultProject.uid : "",
      };
      vm.editClient({
        customLoading: true,
        params: { client: vm.currentClient.uid },
        data,
      })
        .then(() => {
          const successText = defaultProject
            ? vm.$t("toasts.toggleDefaultProject.setSuccess")
            : vm.$t("toasts.toggleDefaultProject.resetSuccess");
          vm.$snotify.success(successText, vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("toasts.toggleDefaultProject.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
  },
  computed: {
    filteredCurrentClientCustomFields() {
      return Object.fromEntries(
        Object.entries(this.currentClientCustomFields).filter(([, field]) => field.visible === true)
      );
    },
    ...mapGetters({
      teams: "auth_get_teams",
      teamsSettings: "global_get_teams_settings",
      currentClient: "clients_get_current_client",
      currentClientProjects: "clients_get_current_client_projects",
      currentClientTags: "clients_get_current_client_tags",
      currentClientCustomFields: "clients_get_current_client_custom_fields",
      tagsPagination: "clients_get_tags_pagination",
      projectsStati: "projects_get_projects_stati",
      currentClientSubscription: "subscriptions_get_current_client_subscription",
      templates: "templates_get_templates",
      contacts: "teams_get_contacts",
      users: "user_get_users",
      projectsCustomFields: "global_get_projects_custom_fields",
      teamTags: "teams_get_tags",
    }),
  },
};
</script>
